import type { SafePromise } from '@benzinga/safe-await';
import type { AdvancedNewsManager } from '@benzinga/advanced-news-manager';
import type { Session } from '@benzinga/session';
import type { NewsDisplayOutputTypes } from '@benzinga/internal-news-manager';
import type { ArticleBlock, ArticleData } from '../entities/article';
import { BasicNewsManager, News, NodeQueryParams } from '@benzinga/basic-news-manager';
import { filterRelatedNodes, getArticleChannelsNames, getArticleTickers } from './taxonomy';
import { findAllAnchorHrefs } from './content';
import { extractArticleNodeIdFromHref } from './url';

export type RelatedArticlesOptions = {
  length?: number;
  page?: number;
  pageSize?: number;
  displayOutput?: NewsDisplayOutputTypes;
};

export const getRelatedArticles = async (
  session: Session,
  articleData: ArticleData,
  advancedNewsManager: AdvancedNewsManager,
  options?: RelatedArticlesOptions,
): SafePromise<News[]> => {
  const { Story } = await import('@benzinga/advanced-news-manager');
  const article = new Story(articleData as unknown as any, advancedNewsManager, undefined);

  const params: NodeQueryParams = {
    displayOutput: options?.displayOutput || 'abstract',
    page: options?.page ?? 0,
    pageSize: options?.pageSize || 100,
    sort: undefined,
  };

  const tickers = getArticleTickers(article);
  if (tickers.length) {
    params['symbols'] = [tickers[0]];
  }

  const channels = getArticleChannelsNames(article);
  if (channels.length) {
    params['channels'] = [channels[0]];
  }

  const basicNewsManager = session.getManager(BasicNewsManager);
  const newsResponse = await basicNewsManager.fetchNodes(params);

  if (newsResponse.err) {
    return newsResponse;
  } else {
    const relatedArticles = filterRelatedNodes(article, newsResponse.ok);

    return { ok: relatedArticles.slice(0, options?.length || 4) };
  }
};

export const getReadNextData = (block: { tagAttributes: { [key: string]: any }; childBlocks: ArticleBlock[] }) => {
  const variant = block.tagAttributes['data-variant'] ?? 'list';
  const newsSelectionMode = block.tagAttributes['data-news-mode'] === 'query' ? 'query' : 'manual';
  const channelQuery = block.tagAttributes['data-channels'] ?? null;
  const tagQuery = block.tagAttributes['data-tags'] ?? null;
  const limitQuery = block.tagAttributes['data-limit'] ? Number(block.tagAttributes['data-limit']) : 1;

  const queryAndOptions = {
    options: {
      limit: limitQuery,
    },
    query: {
      channels: channelQuery,
      tags: tagQuery,
    },
  };

  const title = variant !== 'banner' ? 'Read Next' : '';

  const links = findAllAnchorHrefs(block as unknown as ArticleBlock);
  const articleNodes = links.map(link => extractArticleNodeIdFromHref(link.href)).filter(node => node !== null);

  return { articleNodes, links, newsSelectionMode, queryAndOptions, title, variant };
};
